// Renders the full skip-trace UI stack for a surface, driven by a
// useSkipTrace() controller: the load-not-honored red guard, the submit
// error toast, the persistent stackable run panels, and the
// session-blocked banner (paste Cookie-Editor export → refresh → auto
// re-submit, or "Retry without refresh"). Place it near the top of the
// surface's content so an in-flight batch can't be missed.

import { SkipTraceRunPanel } from './SkipTraceRunPanel';
import { formatAge } from '../services/agentApi';
import type { SkipTraceController } from './useSkipTrace';

export function SkipTracePanels({ skip }: { skip: SkipTraceController }) {
  return (
    <>
      {skip.loadNotHonored && (
        <div className="mt-3 bg-red/15 border border-red rounded px-4 py-3 flex items-start justify-between gap-4">
          <div className="text-[13px] text-red font-medium">{skip.loadNotHonored}</div>
          <button onClick={() => skip.setLoadNotHonored(null)}
            className="text-text-faint hover:text-text text-base leading-none shrink-0">×</button>
        </div>
      )}

      {skip.skipMsg && (
        <div className="mt-3 bg-red/10 border border-red/40 rounded px-4 py-2 flex items-center justify-between gap-4">
          <span className="text-[12px] font-mono text-red">{skip.skipMsg}</span>
          <button onClick={() => skip.setSkipMsg(null)}
            className="text-text-faint hover:text-text text-sm leading-none shrink-0">×</button>
        </div>
      )}

      {skip.runs.length > 0 && (
        <div className="mt-3 space-y-3">
          {skip.runs.map(r => (
            <SkipTraceRunPanel key={r.runId}
              runId={r.runId} label={r.label} startedAt={r.startedAt} queued={r.queued}
              onDismiss={skip.dismissRun} />
          ))}
        </div>
      )}

      {skip.sessionBlocked && (() => {
        const age = skip.weldSession?.age
          ?? (skip.weldSession?.ageSeconds != null ? formatAge(skip.weldSession.ageSeconds) : null);
        return (
          <div className="mt-3 bg-amber/10 border border-amber/50 rounded px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="text-[13px] text-amber">
                Weld session expired — run the session refresh script on your desktop, then retry.
                {age && <span className="text-text-faint font-mono ml-2">(session age {age})</span>}
                {skip.blockReason && <div className="text-[11px] text-text-faint font-mono mt-1">{skip.blockReason}</div>}
              </div>
              <button onClick={() => skip.setSessionBlocked(false)}
                className="text-text-faint hover:text-text text-sm leading-none shrink-0">×</button>
            </div>
            <div className="mt-3">
              <label className="block text-[11px] font-mono text-text-faint mb-1">
                Paste Weld session JSON (Cookie-Editor → Export)
              </label>
              <textarea
                value={skip.sessionJsonDraft}
                onChange={e => skip.setSessionJsonDraft(e.target.value)}
                rows={4}
                spellCheck={false}
                placeholder='[{"name":"…","value":"…","domain":"…"}, …]'
                className="w-full bg-bg-input border border-border rounded px-2 py-1.5 text-[11px] font-mono text-text resize-y" />
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <button onClick={skip.refreshSession} disabled={skip.refreshing || !skip.sessionJsonDraft.trim()}
                  className="px-3 py-1.5 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
                  {skip.refreshing ? 'Validating…' : 'Refresh session'}
                </button>
                <button onClick={skip.retryPending} disabled={skip.skipBusy || !skip.hasPending}
                  title={skip.hasPending ? 'Retry without refreshing (if you refreshed on the desktop)' : 'Nothing to retry'}
                  className="px-3 py-1.5 text-[12px] text-amber border border-amber/50 rounded hover:bg-amber/10 disabled:opacity-50">
                  {skip.skipBusy ? 'Retrying…' : 'Retry without refresh'}
                </button>
                {skip.refreshResult && (
                  <span className={'text-[12px] font-mono ' + (skip.refreshResult.ok ? 'text-green' : 'text-red')}>
                    {skip.refreshResult.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
